import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { formatDate, formatDateTime } from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Badge, Button, Card, Empty, ErrorNote, Field, Loading, Modal, Note, inputClass,
} from "@/components/portal/Ui";

const ROLES = [
  { value: "admin", label: "Administrator", plural: "Administrators",
    blurb: "Full access, including account management and demo data." },
  { value: "internal", label: "Internal staff", plural: "Internal staff",
    blurb: "Run campaigns, chase submissions and approve invoices." },
  { value: "client", label: "Client", plural: "Clients",
    blurb: "See progress and performance for their own campaigns only." },
  { value: "creator", label: "Creator", plural: "Creators / KOLs",
    blurb: "Submit posting links, insights and invoices for assigned campaigns." },
];
const PLATFORMS = ["Instagram", "XiaoHongShu", "TikTok"];

function UserSection({ role, users, isAdmin, currentUserId, onEdit, onResend, onToggle, onDelete }) {
  return (
    <Card testId={`users-card-${role.value}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-extrabold">{role.plural}</h2>
        <span className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]" data-testid={`user-count-${role.value}`}>
          {users.length} account{users.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="mt-1 text-sm body-text">{role.blurb}</p>

      {users.length === 0 ? (
        <div className="mt-4">
          <Empty
            testId={`no-users-${role.value}`}
            title={`No ${role.plural.toLowerCase()} yet`}
            body={`Use "Invite user" and pick the ${role.label} role.`}
          />
        </div>
      ) : (
        <div className="scroll-x mt-4">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                <th className="pb-2.5 pr-4">Name</th>
                <th className="pb-2.5 pr-4">Email</th>
                {role.value === "client" && <th className="pb-2.5 pr-4">Organisation</th>}
                {role.value === "creator" && <th className="pb-2.5 pr-4">Phone</th>}
                <th className="pb-2.5 pr-4">Status</th>
                <th className="pb-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b border-[#F5F1EA]" data-testid={`user-row-${user.id}`}>
                  <td className="py-3 pr-4 font-semibold">
                    {user.name}
                    {user.id === currentUserId && (
                      <span className="ml-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                        You
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4">{user.email}</td>
                  {role.value === "client" && (
                    <td className="py-3 pr-4">{user.client_org_name || "—"}</td>
                  )}
                  {role.value === "creator" && <td className="py-3 pr-4">{user.phone || "—"}</td>}
                  <td className="py-3 pr-4">
                    <Badge tone={user.is_active ? "green" : "neutral"} testId={`user-status-${user.id}`}>
                      {user.is_active ? "Active" : "Pending / inactive"}
                    </Badge>
                  </td>
                  <td className="py-3">
                    <div className="flex flex-col gap-1.5">
                      <button
                        type="button"
                        onClick={() => onEdit(user)}
                        data-testid={`edit-user-${user.id}`}
                        className="text-left text-xs font-semibold text-[#111111] hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onResend(user)}
                        data-testid={`resend-invite-${user.id}`}
                        className="text-left text-xs font-semibold text-[#F2811D] hover:underline"
                      >
                        Send password link
                      </button>
                      {user.id !== currentUserId && (
                        <button
                          type="button"
                          onClick={() => onToggle(user)}
                          data-testid={`toggle-active-${user.id}`}
                          className="text-left text-xs font-semibold text-[#B3261E] hover:underline"
                        >
                          {user.is_active ? "Deactivate" : "Reactivate"}
                        </button>
                      )}
                      {isAdmin && user.id !== currentUserId && (
                        <button
                          type="button"
                          onClick={() => onDelete(user)}
                          data-testid={`delete-user-${user.id}`}
                          className="text-left text-xs font-semibold text-[#B3261E] hover:underline"
                        >
                          Delete account
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const IMPACT_LABELS = {
  campaign_assignments: "campaign assignment",
  posts: "submitted post",
  insight_snapshots: "insight snapshot",
  invoices: "invoice",
  linked_applications: "linked creator application",
  creator_profile: "creator profile",
};

function DeleteUserModal({ user, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) {
      setImpact(null);
      setConfirm("");
      setError("");
      return;
    }
    api
      .get(`/users/${user.id}/deletion-impact`)
      .then(({ data }) => setImpact(data))
      .catch((e) => setError(apiError(e)));
  }, [user]);

  const remove = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/users/${user.id}`, { params: { confirm: confirm.trim() } });
      toast.success(`${user.name}'s account was deleted`);
      await onDeleted();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const linked = Object.entries(impact?.impact || {}).filter(([, count]) => count > 0);

  return (
    <Modal open={Boolean(user)} onClose={onClose} title="Delete account" testId="delete-user-modal">
      <form onSubmit={remove} className="space-y-4" data-testid="delete-user-form">
        <ErrorNote message={error} />
        <Note tone="red">
          This permanently deletes <strong>{user?.name}</strong> ({user?.email}) and everything the
          account owns. It cannot be undone. If you only want to block access, cancel and use
          <strong> Deactivate</strong> instead — that keeps their work in campaign reports.
        </Note>

        {linked.length > 0 && (
          <div className="rounded-xl border border-[#EFEAE1] bg-[#FBF9F5] p-4" data-testid="delete-impact">
            <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
              Will also be removed
            </p>
            <ul className="mt-2 space-y-1 text-sm body-text">
              {linked.map(([key, count]) => (
                <li key={key} data-testid={`delete-impact-${key}`}>
                  {count} {IMPACT_LABELS[key] || key}
                  {count === 1 ? "" : "s"}
                  {key === "linked_applications" ? " (unlinked, application kept)" : ""}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs body-text">
              Campaign totals and client reports will no longer include these numbers.
            </p>
          </div>
        )}
        {impact && linked.length === 0 && (
          <Note tone="neutral" testId="delete-impact-empty">
            This account has no campaign work attached, so nothing else is affected.
          </Note>
        )}

        <Field label={`Type ${user?.email || "the email address"} to confirm`} required>
          <input
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={inputClass}
            data-testid="delete-user-confirm-input"
            autoComplete="off"
          />
        </Field>
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="delete-user-cancel">
            Cancel
          </Button>
          <Button type="submit" variant="danger" busy={busy} testId="delete-user-submit">
            Delete permanently
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default function UsersClients() {
  const [users, setUsers] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [orgModal, setOrgModal] = useState(null);
  const [inviteLink, setInviteLink] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [activityKey, setActivityKey] = useState(0);
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";

  const load = useCallback(async () => {
    setRefreshing(true);
    setActivityKey((k) => k + 1);
    try {
      const [u, o, e] = await Promise.all([
        api.get("/users"),
        api.get("/client-orgs"),
        api.get("/auth/email-status"),
      ]);
      setUsers(u.data);
      setOrgs(o.data);
      setEmailStatus(e.data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setUsers((prev) => prev || []);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (user) => {
    try {
      await api.patch(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(user.is_active ? "Account deactivated" : "Account reactivated");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resend = async (user) => {
    try {
      const { data } = await api.post(`/users/${user.id}/resend-invite`);
      setInviteLink({ email: user.email, ...data.delivery });
      if (data.delivery.sent) toast.success("Activation email sent");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (users === null) return <Loading label="Loading users…" />;

  return (
    <div data-testid="users-clients-page">
      <PageHeader
        title="Users & Clients"
        description="Invite accounts, manage client organisations, and deactivate or delete access. Accounts are grouped by role, and roles are set here — users can never change their own role."
        onRefresh={load}
        refreshing={refreshing}
        actions={
          <>
            <Button variant="outline" testId="new-org-button" onClick={() => setOrgModal({})}>
              <Plus className="h-4 w-4" />
              Client organisation
            </Button>
            <Button testId="new-user-button" onClick={() => setUserModal(true)}>
              <Plus className="h-4 w-4" />
              Invite user
            </Button>
          </>
        }
      />
      <ErrorNote message={error} onRetry={load} />

      {emailStatus && !emailStatus.configured && (
        <div className="mb-5">
          <Note tone="orange" testId="email-not-configured-note">
            Email delivery is not switched on yet, so invitation and password links will not be
            sent. The sender is already set to <strong>{emailStatus.sender}</strong> — all that's
            missing is <strong>RESEND_API_KEY</strong> in the backend environment (and a verified
            anytribemarketing.com domain in Resend). Until then, copy the link shown after inviting
            a user and send it yourself.
          </Note>
        </div>
      )}
      {emailStatus?.configured && (
        <div className="mb-5">
          <Note tone="green" testId="email-configured-note">
            Invitation and password links are being sent from{" "}
            <strong>{emailStatus.sender}</strong>. Users cannot reset their own password — use{" "}
            <strong>Send password link</strong> on their row when they ask you for help.
          </Note>
        </div>
      )}

      <Card className="mb-5" testId="client-orgs-card">
        <h2 className="text-lg font-extrabold">Client organisations</h2>
        {orgs.length === 0 ? (
          <div className="mt-4">
            <Empty testId="no-orgs" title="No client organisations yet" body="Create one before adding client users or campaigns." />
          </div>
        ) : (
          <div className="scroll-x mt-4">
            <table className="w-full min-w-[620px] text-sm">
              <thead>
                <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                  <th className="pb-2.5 pr-4">Organisation</th>
                  <th className="pb-2.5 pr-4">Industry</th>
                  <th className="pb-2.5 pr-4">Campaigns</th>
                  <th className="pb-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => (
                  <tr key={org.id} className="border-b border-[#F5F1EA]" data-testid={`org-row-${org.id}`}>
                    <td className="py-3 pr-4 font-semibold">{org.name}</td>
                    <td className="py-3 pr-4">{org.industry || "—"}</td>
                    <td className="py-3 pr-4 tabular">{org.campaign_count}</td>
                    <td className="py-3">
                      <button
                        type="button"
                        onClick={() => setOrgModal(org)}
                        data-testid={`edit-org-${org.id}`}
                        className="text-xs font-semibold text-[#F2811D] hover:underline"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="space-y-5" data-testid="user-sections">
        {ROLES.map((role) => (
          <UserSection
            key={role.value}
            role={role}
            users={users.filter((u) => u.role === role.value)}
            isAdmin={isAdmin}
            currentUserId={me?.id}
            onEdit={setEditUser}
            onResend={resend}
            onToggle={toggleActive}
            onDelete={setDeleteUser}
          />
        ))}
      </div>

      <UserModal
        open={userModal}
        orgs={orgs}
        onClose={() => setUserModal(false)}
        onSaved={async (delivery, email) => {
          setUserModal(false);
          setInviteLink({ email, ...delivery });
          await load();
        }}
      />

      <OrgModal
        org={orgModal}
        onClose={() => setOrgModal(null)}
        onSaved={async () => {
          setOrgModal(null);
          await load();
        }}
      />

      <EditUserModal
        user={editUser}
        onClose={() => setEditUser(null)}
        onSaved={async () => {
          setEditUser(null);
          await load();
        }}
      />

      <DeleteUserModal
        user={deleteUser}
        onClose={() => setDeleteUser(null)}
        onDeleted={async () => {
          setDeleteUser(null);
          await load();
        }}
      />

      {isAdmin && <AccountActivityCard refreshKey={activityKey} />}

      <DemoDataCard onPurged={load} />
      <Modal
        open={Boolean(inviteLink)}
        onClose={() => setInviteLink(null)}
        title="Activation link"
        testId="invite-link-modal"
      >
        <div className="space-y-4">
          {inviteLink?.sent ? (
            <Note tone="green">An activation email was sent to {inviteLink.email}.</Note>
          ) : (
            <Note tone="orange" testId="invite-delivery-error">
              {inviteLink?.error || "Email could not be delivered."}
            </Note>
          )}
          <Field label="Activation link" hint="Valid for 7 days. Share it only with the account holder.">
            <textarea
              readOnly
              rows={3}
              value={inviteLink?.link || ""}
              className={inputClass}
              data-testid="invite-link-text"
              onFocus={(e) => e.target.select()}
            />
          </Field>
          <Button
            testId="copy-invite-link"
            onClick={() => {
              navigator.clipboard?.writeText(inviteLink?.link || "");
              toast.success("Link copied");
            }}
          >
            Copy link
          </Button>
        </div>
      </Modal>
    </div>
  );
}

const ACTION_TONE = (action) => {
  if (action.startsWith("deleted")) return "text-[#B3261E]";
  if (action.startsWith("invited")) return "text-[#F2811D]";
  return "text-[#111111]";
};

function AccountActivityCard({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/users/activity", { params: { limit: 60 } })
      .then(({ data }) => {
        setRows(data);
        setError("");
      })
      .catch((e) => setError(apiError(e)));
  }, [refreshKey]);

  return (
    <Card className="mt-5" testId="account-activity-card">
      <h2 className="text-lg font-extrabold">Account activity</h2>
      <p className="mt-1 text-sm body-text">
        Who invited, edited, deactivated or deleted each account, and when. Newest first, in
        Malaysia time.
      </p>
      <ErrorNote message={error} />
      {rows && rows.length === 0 && (
        <div className="mt-4">
          <Empty
            testId="no-account-activity"
            title="No account changes yet"
            body="Inviting, editing, deactivating or deleting an account will be recorded here."
          />
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="scroll-x mt-4">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                <th className="pb-2.5 pr-4">When</th>
                <th className="pb-2.5 pr-4">Who</th>
                <th className="pb-2.5 pr-4">Did what</th>
                <th className="pb-2.5">To which account</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-[#F5F1EA]"
                  data-testid={`account-activity-row-${row.id}`}
                >
                  <td className="py-3 pr-4 whitespace-nowrap">{formatDateTime(row.created_at)}</td>
                  <td className="py-3 pr-4 font-semibold">
                    {row.actor_name || "Unknown"}
                    {row.actor_role && (
                      <span className="ml-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                        {ROLES.find((r) => r.value === row.actor_role)?.label || row.actor_role}
                      </span>
                    )}
                  </td>
                  <td className={`py-3 pr-4 font-semibold ${ACTION_TONE(row.action)}`}>
                    {row.action}
                    {row.changed_fields && (
                      <span className="ml-1 font-normal body-text">({row.changed_fields})</span>
                    )}
                  </td>
                  <td className="py-3">
                    {row.target_email || "—"}
                    {row.target_deleted && (
                      <span className="ml-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                        Deleted
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function DemoDataCard({ onPurged }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/demo-data");
      setStatus(data);
    } catch (e) {
      setStatus({ total: 0 });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const purge = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.delete("/admin/demo-data", {
        params: { confirm: confirm.trim() },
      });
      toast.success(`${data.total} demo records removed`);
      setOpen(false);
      setConfirm("");
      await load();
      await onPurged();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!status || status.total === 0) return null;

  return (
    <>
      <Card className="mt-5" testId="demo-data-card">
        <h2 className="text-lg font-extrabold">Demo data</h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed body-text">
          This workspace contains <strong>{status.total}</strong> clearly-labelled fictional records
          (accounts marked "(Demo)", 2 client organisations, 4 creators, 3 campaigns, posts,
          insights and invoices). The demo accounts share one password, so remove them before real
          clients and creators start using the live app. Your own data is not affected.
        </p>
        <div className="mt-4">
          <Button variant="danger" testId="purge-demo-data-button" onClick={() => setOpen(true)}>
            Remove all demo data
          </Button>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Remove all demo data" testId="purge-demo-modal">
        <form onSubmit={purge} className="space-y-4" data-testid="purge-demo-form">
          <ErrorNote message={error} />
          <Note tone="red">
            This permanently deletes all {status.total} records flagged as demo data, including the
            demo user accounts. It cannot be undone. Real campaigns, creators, clients and invoices
            are left untouched.
          </Note>
          <Field label='Type "DELETE DEMO DATA" to confirm' required>
            <input
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputClass}
              data-testid="purge-demo-confirm-input"
            />
          </Field>
          <div className="flex justify-end gap-2.5">
            <Button variant="outline" onClick={() => setOpen(false)} testId="purge-demo-cancel">
              Cancel
            </Button>
            <Button type="submit" variant="danger" busy={busy} testId="purge-demo-submit">
              Remove demo data
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}

function UserModal({ open, orgs, onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", role: "creator", client_org_id: "", phone: "" });
  const [socials, setSocials] = useState([]);
  const [waConsent, setWaConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ email: "", name: "", role: "creator", client_org_id: "", phone: "" });
      setSocials([]);
      setError("");
    }
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/users", {
        email: form.email.trim(),
        name: form.name.trim(),
        role: form.role,
        client_org_id: form.role === "client" ? form.client_org_id : null,
        phone: form.phone.trim() || null,
        socials: form.role === "creator" ? socials.filter((s) => s.handle.trim()) : [],
        whatsapp_consent: form.role === "creator" ? waConsent : undefined,
      });
      toast.success("Account created and invited");
      onSaved(data.delivery, form.email.trim());
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Invite a user" testId="user-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="user-form">
        <ErrorNote message={error} />
        <Note tone="neutral">
          The account starts inactive. It becomes usable only once the person sets a password
          through their activation link.
        </Note>
        <Field label="Full name" required>
          <input required value={form.name} onChange={set("name")} className={inputClass} data-testid="user-name-input" />
        </Field>
        <Field label="Email address" required>
          <input type="email" required value={form.email} onChange={set("email")} className={inputClass} data-testid="user-email-input" />
        </Field>
        <Field label="Role" required hint="Roles are assigned by administrators only.">
          <select value={form.role} onChange={set("role")} className={inputClass} data-testid="user-role-select">
            {ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        </Field>
        {form.role === "client" && (
          <Field label="Client organisation" required>
            <select required value={form.client_org_id} onChange={set("client_org_id")} className={inputClass} data-testid="user-org-select">
              <option value="">Select an organisation</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Phone number (optional)">
          <input value={form.phone} onChange={set("phone")} className={inputClass} data-testid="user-phone-input" />
        </Field>
        {form.role === "creator" && (
          <div>
            <span className="mb-1.5 block text-sm font-semibold">Social platforms</span>
            <div className="space-y-2.5">
              {socials.map((social, index) => (
                <div key={index} className="flex gap-2.5">
                  <select
                    value={social.platform}
                    onChange={(e) =>
                      setSocials((list) => list.map((s, i) => (i === index ? { ...s, platform: e.target.value } : s)))
                    }
                    className={inputClass}
                    data-testid={`user-social-platform-${index}`}
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <input
                    value={social.handle}
                    placeholder="@handle"
                    onChange={(e) =>
                      setSocials((list) => list.map((s, i) => (i === index ? { ...s, handle: e.target.value } : s)))
                    }
                    className={inputClass}
                    data-testid={`user-social-handle-${index}`}
                  />
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              className="mt-2.5"
              testId="user-social-add"
              onClick={() => setSocials((list) => [...list, { platform: "Instagram", handle: "" }])}
            >
              Add platform
            </Button>
          </div>
        )}
        <div className="flex justify-end gap-2.5 pt-1">
          <Button variant="outline" onClick={onClose} testId="user-cancel-button">
            Cancel
          </Button>
          {form.role === "creator" && (
            <label className="flex items-start gap-2.5 rounded-xl bg-[#F7F4EF] p-3.5">
              <input
                type="checkbox"
                checked={waConsent}
                onChange={(e) => setWaConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#F2811D]"
                data-testid="user-whatsapp-consent"
              />
              <span className="text-sm leading-relaxed body-text">
                This creator has agreed to receive WhatsApp messages from AnyTribe. Payment
                confirmations are blocked without it.
              </span>
            </label>
          )}
          <Button type="submit" busy={busy} testId="user-save-button">
            Create & send invite
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function OrgModal({ org, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [industry, setIndustry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!org) return;
    setName(org.name || "");
    setIndustry(org.industry || "");
    setError("");
  }, [org]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      if (org?.id) await api.patch(`/client-orgs/${org.id}`, { name: name.trim(), industry: industry.trim() });
      else await api.post("/client-orgs", { name: name.trim(), industry: industry.trim() });
      toast.success(org?.id ? "Organisation updated" : "Organisation created");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(org)}
      onClose={onClose}
      title={org?.id ? "Edit client organisation" : "New client organisation"}
      testId="org-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="org-form">
        <ErrorNote message={error} />
        <Field label="Organisation name" required>
          <input required value={name} onChange={(e) => setName(e.target.value)} className={inputClass} data-testid="org-name-input" />
        </Field>
        <Field label="Industry / category">
          <input value={industry} onChange={(e) => setIndustry(e.target.value)} className={inputClass} data-testid="org-industry-input" />
        </Field>
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="org-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="org-save-button">
            {org?.id ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}


function EditUserModal({ user, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isCreator = user?.role === "creator";

  useEffect(() => {
    if (!user) return;
    setName(user.name || "");
    setPhone(user.phone || "");
    setConsent(Boolean(user.whatsapp_consent));
    setError("");
  }, [user]);

  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.patch(`/users/${user.id}`, {
        name,
        phone: phone.trim(),
        whatsapp_consent: isCreator ? consent : undefined,
      });
      toast.success("Account updated");
      await onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      title={`Edit ${user?.name || "account"}`}
      testId="edit-user-modal"
    >
      <form onSubmit={save} className="space-y-4" data-testid="edit-user-form">
        <ErrorNote message={error} testId="edit-user-error" />
        <Note tone="neutral">
          {user?.email} · {user?.role}. Email and role cannot be changed here.
        </Note>
        <Field label="Name" required>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            data-testid="edit-user-name-input"
          />
        </Field>
        <Field
          label="Mobile number"
          hint={isCreator ? "Malaysian numbers only, e.g. 012-345 6789. Needed for WhatsApp payment confirmations." : "Optional."}
        >
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputClass}
            placeholder="012-345 6789"
            data-testid="edit-user-phone-input"
          />
        </Field>
        {isCreator && (
          <label className="flex items-start gap-2.5 rounded-xl bg-[#F7F4EF] p-3.5">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[#F2811D]"
              data-testid="user-whatsapp-consent"
            />
            <span className="text-sm leading-relaxed body-text">
              This creator has agreed to receive WhatsApp messages from AnyTribe. Payment
              confirmations stay blocked without it.
            </span>
          </label>
        )}
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="edit-user-cancel">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="edit-user-save">
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
